/**
 * impersonationSlice — backoffice impersonation banner display state.
 *
 * This slice holds DISPLAY STATE ONLY. It drives the persistent impersonation
 * banner shown to Hostesa operators when they are acting on behalf of a hotel user.
 *
 * SECURITY MODEL:
 * - This slice NEVER grants permissions. All authorization is performed by the
 *   backend; the frontend banner is a usability/awareness affordance only.
 * - Requires admin:impersonate permission (checked in packages/auth-client) before
 *   start() can be called by the consuming app.
 * - impersonatedUserLabel is a display-only string (e.g. a role + hotel name like
 *   "Hotel Admin — Vila Rila"). It MUST NOT contain email addresses, phone numbers,
 *   or any guest/personal identifiers.
 * - Redux DevTools sanitizer (see store.ts) strips this field from DevTools snapshots
 *   in production to prevent accidental PII leakage via operator tooling.
 * - startedAt is an ISO timestamp string so it is natively serializable.
 */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export interface ImpersonationState {
  /** Whether an impersonation session is currently active. */
  active: boolean;
  /**
   * A human-readable label describing WHO is being impersonated.
   * MUST be a non-PII display string such as "Hotel Admin — Vila Rila".
   * Must NOT contain email, phone number, guest name, or credential fragments.
   */
  impersonatedUserLabel: string | null;
  /** ISO 8601 timestamp when the impersonation session started. */
  startedAt: string | null;
}

const initialState: ImpersonationState = {
  active: false,
  impersonatedUserLabel: null,
  startedAt: null,
};

export interface StartImpersonationPayload {
  /**
   * Non-PII display label for the impersonated context.
   * Example: "Hotel Admin — Vila Rila" or "Hotel Staff — Grand Palace".
   * Must NOT include emails, phone numbers, or personal identifiers.
   */
  impersonatedUserLabel: string;
  /** ISO 8601 timestamp. Defaults to now if not provided. */
  startedAt?: string;
}

export const impersonationSlice = createSlice({
  name: 'impersonation',
  initialState,
  reducers: {
    /**
     * Activate impersonation display state.
     * The consuming app must verify admin:impersonate permission before calling this.
     */
    startImpersonation(state, action: PayloadAction<StartImpersonationPayload>) {
      state.active = true;
      state.impersonatedUserLabel = action.payload.impersonatedUserLabel;
      state.startedAt = action.payload.startedAt ?? new Date().toISOString();
    },
    /**
     * Deactivate impersonation display state and clear all fields.
     * Call this when the operator exits the impersonation session.
     */
    stopImpersonation(state) {
      state.active = false;
      state.impersonatedUserLabel = null;
      state.startedAt = null;
    },
    /**
     * Alias for stopImpersonation — resets slice to initialState.
     * Useful for logout flows that clear all state.
     */
    clearImpersonation(state) {
      state.active = false;
      state.impersonatedUserLabel = null;
      state.startedAt = null;
    },
  },
});

export const { startImpersonation, stopImpersonation, clearImpersonation } =
  impersonationSlice.actions;

// Selectors
export const selectImpersonationActive = (state: { impersonation: ImpersonationState }) =>
  state.impersonation.active;

export const selectImpersonatedUserLabel = (state: { impersonation: ImpersonationState }) =>
  state.impersonation.impersonatedUserLabel;

export const selectImpersonationStartedAt = (state: { impersonation: ImpersonationState }) =>
  state.impersonation.startedAt;
